'use client';

import clsx from 'clsx';
import { AnimatePresence, motion } from 'motion/react';
import { useAriaLabels } from '@/hooks/useAriaLabels';
import { useTranslations } from 'next-intl';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';

import { MediaWrapper } from '@/components/cms/MediaWrapper';
import { Button } from '@/components/ui/Button/Button';
import { Icon } from '@/components/ui/Icon/Icon';
import { modalActions } from '@/states/modalState';

import styles from './Timeline.module.scss';
import type { TimelineProps } from './Timeline.types';
import { crossfadeTransition, readHeaderHeight, toTimelineData } from './Timeline.utils';

export type { TimelineProps } from './Timeline.types';

export function Timeline({
  className,
  cmsIndex,
  initialIndex = 0,
  id,
  entries,
  ctaLabel,
  stickyScroll = false,
}: TimelineProps) {
  const t = useTranslations('timeline');
  const aria = useAriaLabels();

  const sectionRef = useRef<HTMLElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const timelineData = toTimelineData(entries);

  const entryCount = timelineData.length;

  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isTimeWheelOpen, setIsTimeWheelOpen] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mql.matches);

    const onChange = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  const scrollHijackEnabled = stickyScroll && !prefersReducedMotion;

  useEffect(() => {
    if (!scrollHijackEnabled) return;
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    let frameId: number | null = null;
    const update = () => {
      frameId = null;
      const rect = wrapper.getBoundingClientRect();
      // On mobile the sticky tile pins at top: 0 (see SCSS), so the
      // progress math must use headerH = 0 too. Desktop keeps the
      // header offset.
      const isDesktop = window.matchMedia('(min-width: 768px)').matches;
      const headerH = isDesktop ? readHeaderHeight() : 0;
      const usableHeight = rect.height - (window.innerHeight - headerH);
      if (usableHeight <= 0) return;
      const progress = (-rect.top + headerH) / usableHeight;
      const clamped = Math.max(0, Math.min(0.9999, progress));
      const idx = Math.min(entryCount - 1, Math.floor(clamped * entryCount));
      setCurrentIndex(idx);
    };

    const handleScroll = () => {
      if (frameId !== null) return;
      frameId = requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      if (frameId !== null) cancelAnimationFrame(frameId);
    };
  }, [scrollHijackEnabled, entryCount]);

  const scrollToIndex = useCallback(
    (index: number) => {
      const wrapper = wrapperRef.current;
      if (!wrapper) return;
      const rect = wrapper.getBoundingClientRect();
      const isDesktop = window.matchMedia('(min-width: 768px)').matches;
      const headerH = isDesktop ? readHeaderHeight() : 0;
      const usableHeight = rect.height - (window.innerHeight - headerH);
      const wrapperTop = rect.top + window.scrollY;
      const targetScroll = wrapperTop + ((index + 0.5) / entryCount) * usableHeight;
      window.scrollTo({ top: targetScroll, behavior: 'smooth' });
    },
    [entryCount]
  );

  const handleTimeWheelSelect = useCallback(
    (index: number) => {
      setIsTimeWheelOpen(false);
      if (!scrollHijackEnabled || !wrapperRef.current) {
        setCurrentIndex(index);
        return;
      }
      scrollToIndex(index);
    },
    [scrollHijackEnabled, scrollToIndex]
  );

  const testIdPrefix = cmsIndex !== undefined ? `timeline-${cmsIndex}` : undefined;
  const testId = (suffix: string) => (testIdPrefix ? `${testIdPrefix}-${suffix}` : undefined);

  const currentEntry = timelineData[currentIndex];
  const prevEntry = currentIndex > 0 ? timelineData[currentIndex - 1] : null;
  const nextEntry = currentIndex < entryCount - 1 ? timelineData[currentIndex + 1] : null;

  const goToPrev = () => {
    if (currentIndex <= 0) return;
    const newIndex = currentIndex - 1;
    if (scrollHijackEnabled && wrapperRef.current) {
      scrollToIndex(newIndex);
    } else {
      setCurrentIndex(newIndex);
    }
  };

  const goToNext = () => {
    if (currentIndex >= entryCount - 1) return;
    const newIndex = currentIndex + 1;
    if (scrollHijackEnabled && wrapperRef.current) {
      scrollToIndex(newIndex);
    } else {
      setCurrentIndex(newIndex);
    }
  };

  if (stickyScroll && prefersReducedMotion) {
    return (
      <div className={styles.reducedMotionStack}>
        {timelineData.map((entry) => (
          <section
            key={entry.year}
            className={styles.reducedMotionEntry}
            aria-label={aria.action('brandTimeline')}
          >
            <div className={styles.backgroundLayer}>
              <div className={styles.backgroundImage}>
                <MediaWrapper media={entry.backgroundImage} fill disableLink sizes="100vw" />
              </div>
              <div className={styles.overlay} aria-hidden="true" />
            </div>
            <div className={styles.content}>
              <div className={styles.currentYear}>{entry.year}</div>
              <h2 className={styles.title}>{entry.title}</h2>
              {entry.popupId && ctaLabel ? (
                <Button
                  iconAfter={<Icon name="chevron-right" size={16} />}
                  onClick={() =>
                    modalActions.open('cms-popup', {
                      popupId: entry.popupId!,
                      popinTitle: entry.title || `history-${entry.year}`,
                    })
                  }
                >
                  {ctaLabel}
                </Button>
              ) : null}
            </div>
          </section>
        ))}
      </div>
    );
  }

  const sectionEl = (
    <section
      ref={sectionRef}
      id={scrollHijackEnabled ? undefined : (id ?? undefined)}
      className={clsx(styles.timeline, scrollHijackEnabled && styles.timelineSticky, className)}
      aria-label={aria.action('brandTimeline')}
      data-testid={testId('section')}
    >
      {/* Background images with crossfade */}
      <div className={styles.backgroundLayer}>
        <AnimatePresence initial={false}>
          <motion.div
            key={currentEntry.year}
            className={styles.backgroundImage}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, ease: 'easeInOut' }}
          >
            <MediaWrapper
              media={currentEntry.backgroundImage}
              fill
              disableLink
              priority={currentIndex === 0}
              sizes="100vw"
            />
          </motion.div>
        </AnimatePresence>
        <div className={styles.overlay} aria-hidden="true" />
      </div>

      {/* Time Wheel ruler — flat structure for consistent spacing */}
      <div className={styles.timeWheelRuler} aria-hidden="true">
        {/* Leading ticks */}
        <div className={clsx(styles.rulerTick, styles.rulerTickSm)} />
        <div className={clsx(styles.rulerTick, styles.rulerTickMd)} />
        <div className={clsx(styles.rulerTick, styles.rulerTickLg)} />

        {timelineData.map((entry, index) => {
          const isMiddle = index === Math.ceil(timelineData.length / 2) - 1;
          return (
            <Fragment key={entry.year}>
              {/* Orange year line — middle year anchors the Time Wheel trigger */}
              {isMiddle ? (
                <div className={styles.rulerYearLineAnchor}>
                  <div className={styles.rulerYearLine} />
                  <button
                    className={styles.timeWheelTrigger}
                    type="button"
                    onClick={() => setIsTimeWheelOpen(true)}
                    data-testid={testId('time-wheel-trigger')}
                  >
                    <span className={styles.timeWheelArrow}>&#x25C0;</span>
                    <span className={styles.timeWheelLabel}>{t('timeWheel')}</span>
                  </button>
                </div>
              ) : (
                <div className={styles.rulerYearLine} />
              )}

              {/* 5 graduated ticks between years (V-pattern) */}
              {index < timelineData.length - 1 && (
                <>
                  <div className={clsx(styles.rulerTick, styles.rulerTickLg)} />
                  <div className={clsx(styles.rulerTick, styles.rulerTickMd)} />
                  <div className={clsx(styles.rulerTick, styles.rulerTickSm)} />
                  <div className={clsx(styles.rulerTick, styles.rulerTickMd)} />
                  <div className={clsx(styles.rulerTick, styles.rulerTickLg)} />
                </>
              )}
            </Fragment>
          );
        })}

        {/* Trailing ticks */}
        <div className={clsx(styles.rulerTick, styles.rulerTickLg)} />
        <div className={clsx(styles.rulerTick, styles.rulerTickMd)} />
        <div className={clsx(styles.rulerTick, styles.rulerTickSm)} />
      </div>

      {/* Time Wheel drawer */}
      <div
        role="presentation"
        className={clsx(styles.timeWheelOverlay, isTimeWheelOpen && styles.timeWheelOverlayOpen)}
        onClick={() => setIsTimeWheelOpen(false)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setIsTimeWheelOpen(false);
        }}
        aria-hidden={!isTimeWheelOpen}
      />
      <nav
        className={clsx(styles.timeWheelDrawer, isTimeWheelOpen && styles.timeWheelDrawerOpen)}
        aria-label={t('timeWheelNavLabel')}
        aria-hidden={!isTimeWheelOpen}
      >
        {/* Close button — far right of drawer */}
        <button
          className={styles.timeWheelClose}
          type="button"
          onClick={() => setIsTimeWheelOpen(false)}
          aria-label={t('timeWheelClose')}
          tabIndex={isTimeWheelOpen ? 0 : -1}
        >
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* Drawer ruler — mirrors the main ruler with year labels */}
        <div className={styles.drawerRuler}>
          {/* Leading ticks */}
          <div className={clsx(styles.drawerTick, styles.drawerTickSm)} />
          <div className={clsx(styles.drawerTick, styles.drawerTickMd)} />
          <div className={clsx(styles.drawerTick, styles.drawerTickLg)} />

          {timelineData.map((entry, index) => (
            <Fragment key={entry.year}>
              {/* Year row: olive/orange line + year label */}
              <div className={styles.drawerYearRow}>
                <div
                  className={clsx(
                    styles.drawerYearLine,
                    index === currentIndex && styles.drawerYearLineActive
                  )}
                />
                <button
                  className={clsx(
                    styles.drawerYearButton,
                    index === currentIndex && styles.drawerYearButtonActive
                  )}
                  type="button"
                  onClick={() => handleTimeWheelSelect(index)}
                  aria-current={index === currentIndex ? 'true' : undefined}
                  tabIndex={isTimeWheelOpen ? 0 : -1}
                >
                  {entry.year}
                </button>
              </div>

              {/* 5 graduated ticks between years (V-pattern) */}
              {index < timelineData.length - 1 && (
                <>
                  <div className={clsx(styles.drawerTick, styles.drawerTickLg)} />
                  <div className={clsx(styles.drawerTick, styles.drawerTickMd)} />
                  <div className={clsx(styles.drawerTick, styles.drawerTickSm)} />
                  <div className={clsx(styles.drawerTick, styles.drawerTickMd)} />
                  <div className={clsx(styles.drawerTick, styles.drawerTickLg)} />
                </>
              )}
            </Fragment>
          ))}

          {/* Trailing ticks */}
          <div className={clsx(styles.drawerTick, styles.drawerTickLg)} />
          <div className={clsx(styles.drawerTick, styles.drawerTickMd)} />
          <div className={clsx(styles.drawerTick, styles.drawerTickSm)} />
        </div>
      </nav>

      {/* Foreground content */}
      <div className={styles.content}>
        {/* Previous year — always rendered to reserve space, hidden when first */}
        <button
          className={clsx(styles.yearNav, !prevEntry && styles.hidden)}
          onClick={goToPrev}
          aria-label={prevEntry ? `Go to year ${prevEntry.year}` : undefined}
          aria-hidden={!prevEntry}
          tabIndex={prevEntry ? 0 : -1}
          data-testid={testId('prev')}
        >
          {prevEntry?.year ?? currentEntry.year}
        </button>
        <div
          className={clsx(styles.verticalLine, styles.verticalLineTop, !prevEntry && styles.hidden)}
          aria-hidden="true"
        />

        {/* Animated year — crossfade */}
        <div className={styles.yearWrapper}>
          <AnimatePresence initial={false}>
            <motion.div
              key={currentEntry.year}
              className={styles.currentYear}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={crossfadeTransition}
              data-testid={testId('year')}
            >
              {currentEntry.year}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Animated title — crossfade */}
        <div className={styles.titleWrapper}>
          <AnimatePresence initial={false}>
            <motion.h2
              key={`title-${currentEntry.year}`}
              className={styles.title}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={crossfadeTransition}
              data-testid={testId('title')}
            >
              {currentEntry.title}
            </motion.h2>
          </AnimatePresence>
        </div>

        {currentEntry.popupId && ctaLabel ? (
          <Button
            iconAfter={<Icon name="chevron-right" size={16} />}
            data-testid={testId('cta')}
            onClick={() =>
              modalActions.open('cms-popup', {
                popupId: currentEntry.popupId!,
                popinTitle: currentEntry.title || `history-${currentEntry.year}`,
              })
            }
          >
            {ctaLabel}
          </Button>
        ) : null}

        {/* Next year — always rendered to reserve space, hidden when last */}
        <div
          className={clsx(
            styles.verticalLine,
            styles.verticalLineBottom,
            !nextEntry && styles.hidden
          )}
          aria-hidden="true"
        />
        <button
          className={clsx(styles.yearNav, !nextEntry && styles.hidden)}
          onClick={goToNext}
          aria-label={nextEntry ? `Go to year ${nextEntry.year}` : undefined}
          aria-hidden={!nextEntry}
          tabIndex={nextEntry ? 0 : -1}
          data-testid={testId('next')}
        >
          {nextEntry?.year ?? currentEntry.year}
        </button>
      </div>
    </section>
  );

  if (!scrollHijackEnabled) {
    return sectionEl;
  }

  return (
    <div
      ref={wrapperRef}
      id={id ?? undefined}
      className={styles.timelineWrapper}
      style={{ height: `${entryCount * 100}dvh` }}
    >
      {sectionEl}
    </div>
  );
}
